import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonOutline as PersonOutlineIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WSTextField from "../../Components/TextBox/WSTextBox";
import { FormProvider, useForm } from "react-hook-form";
import WSButton from "../../Components/Button/WSButton";
import {
  callAPI,
  Method,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { resetDataComponent } from "../../StateManagement/actions";

export const Role: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;

  const theme = useTheme();
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [loadingSaveAndNew, setLoadingSaveAndNew] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const methods = useForm<any>({
    defaultValues: { 
      roleName: "",
    },
  });
useEffect(() => {
    if (id) {
      setIsEditMode(true);
      loadRoleData(id);
    }
  }, [id]);

  const loadRoleData = async (roleId: string) => {
    try {
      const response = await callAPI(`Vyapar/Role/${roleId}`, Method.GET);

      let roleData: any = null;
      if (response?.data) roleData = response.data;
      else if (response?.result) roleData = response.result;
      else roleData = response;

      if (roleData && Object.keys(roleData).length > 0) {
        reset({
        
          roleName: roleData.roleName || "",

        });
      } else {
        showMessage("No role data found", "warning");
      }
    } catch (error: any) {
      showMessage(
        "Failed to fetch customer data: " + (error.message || "Unknown error"),
        "error"
      );
    }
  };

  
  const { handleSubmit, reset } = methods;
const handleDataSaveAndNew = useCallback(
    async (data: any) => {
      try {
        setLoadingSaveAndNew(true);
        const request = prepareRequestData(data);
        await callAPI("Vyapar/Role", Method.POST, request);
        reset({
          roleName: "",
        });
        showMessage("Role created successfully", "success");
      } catch (error) {
        showMessage("Failed to create customer", "error");
      } finally {
        setLoadingSaveAndNew(false);
      }
    },
    [reset, showMessage]
  );
  const prepareRequestData = (data: any) => ({
    roleName: data.roleName || "",
  });
  
  const handleData = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        const request = prepareRequestData(data);

        let response;
        if (isEditMode && id) {
          response = await callAPI(
            `Vyapar/Role/${id}`,
            Method.PUT,
            request
          );
        } else {
          response = await callAPI("Vyapar/Role", Method.POST, request);
        }

        dispatch(resetDataComponent("roleGrid"));
        showMessage(
          response?.message ||
            (isEditMode
              ? "Role updated successfully"
              : "Role created successfully"),
          "success"
        );

        navigate("/role/list");
      } catch (error) {
        showMessage("Failed to save role", "error");
      } finally {
        setLoading(false);
      }
    },
    [id, isEditMode, dispatch, showMessage, navigate]
  );

 

  const handleClose = () => navigate("/role/list");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleData)}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              px: 3,
              py: 2,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 40,
                      height: 40,
                      boxShadow: `0 4px 10px ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}`,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {isEditMode ? "Edit Role" : "Add New Role"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isEditMode
                        ? "Update Role information"
                        : "Enter Role details to create a new record"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item>
                <Tooltip title="Close">
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  label="Role Name"
                  {...methods.register("partyName")}
                  id="roleName"
                  name="roleName"
                  control={methods.control}
                  rules={{ required: "Role Name is required" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 3px ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )}`,
                      },
                    },
                  }}
                  suffix={
                    <PersonOutlineIcon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 20,
                      }}
                    />
                  }
                />
              </Grid>

          
            </Grid>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Tooltip title="Save and add another customer">
            <WSButton
              id="saveAndNewBtn"
              variant="outlined"
              color="secondary"
              size="medium"
              label="Save & New"
              onClick={handleSubmit(handleDataSaveAndNew)}
              loading={loadingSaveAndNew}
              startIcon={<AddIcon />}
            />
          </Tooltip>

          <Tooltip title="Save">
            <WSButton
              id="saveBtn"
              variant="contained"
              color="primary"
              size="medium"
              label="Save Role"
              type="submit"
              startIcon={<SaveIcon />}
              loading={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  boxShadow: `0 6px 16px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            />
          </Tooltip>
        </Box>
      </form>
    </FormProvider>
  );
});

export default Role;
